import {
  anchorEnd,
  anchorStart,
  assignSpanLanes,
  clusterByPosition,
  COMMENT_SPAN_LANES,
  formatClusterCount,
  type MediaCommentAnchor,
} from '@lib/media/commentAnchor';
import classNames from 'classnames';
import { useMemo, type CSSProperties, type ReactNode } from 'react';
import { pixelAtTime } from '../../timelineGeometry';
import css from './CommentMarkers.module.scss';

export type TimelineCommentMark = { id: string; anchor: MediaCommentAnchor };

type Props = {
  /** Full scrollable width of the strip in pixels. */
  timelineWidth: number;
  /** Media duration in seconds. */
  duration: number;
  marks: TimelineCommentMark[];
  selectedCommentId: string | null;
  onSelect: (commentId: string) => void;
};

/** Narrower than this and a short span is not something a fingertip can land on. */
const MIN_MARK_WIDTH_PX = 6;

/**
 * How close two frame dots can be, in px, before they are drawn as one mark with a count. The dot
 * is 8px (`.markPoint`) with a 1.5px ring either side. Zooming the strip in pulls a group apart
 * again, since the gap is measured on screen rather than in seconds.
 */
const DOT_CLUSTER_PX = 11;

/**
 * Where every comment points, drawn along the bottom of the filmstrip.
 *
 * An absolutely-positioned overlay inside the strip's scrollable width, like `TimestampNeedle`
 * and `MomentHead` — which is what makes it scroll and zoom with the frames for free, and why
 * it reads its positions from `pixelAtTime` rather than working them out again. The filmstrip
 * always spans the whole media, so these are absolute seconds with no clip to map through; the
 * scrub bar's copy of this job does have one (see `VideoPlayer`'s `commentMarks`).
 *
 * The scrub bar carries the same marks a few pixels higher. Both are worth having: the bar is
 * where someone scrubbing is looking, and the strip is where they go when a note is a second
 * long and the bar cannot separate it from its neighbour.
 */
export const CommentMarkers = ({
  timelineWidth,
  duration,
  marks,
  selectedCommentId,
  onSelect,
}: Props) => {
  // The strip re-renders on every playhead tick; none of this depends on the playhead.
  const layout = useMemo(() => {
    if (!marks.length || duration <= 0 || timelineWidth <= 0) return null;
    const geometry = { timelineWidth, duration };
    return {
      geometry,
      lanes: assignSpanLanes(marks, COMMENT_SPAN_LANES),
      spans: marks.filter((mark) => mark.anchor.type === 'range'),
      dotGroups: clusterByPosition(
        marks
          .filter((mark) => mark.anchor.type === 'frame')
          .map((mark) => ({ mark, position: pixelAtTime(anchorStart(mark.anchor), geometry) })),
        DOT_CLUSTER_PX,
      ),
    };
  }, [marks, timelineWidth, duration]);
  if (!layout) return null;
  const { geometry, lanes, spans, dotGroups } = layout;

  // Spans first and dot groups after — in DOM order as well as z-order, as on the scrub bar — so a
  // dot on a span's first frame is drawn over the span instead of inside it.
  return (
    <div className={css.layer}>
      {spans.map((mark) => {
        const start = pixelAtTime(anchorStart(mark.anchor), geometry);
        const span = pixelAtTime(anchorEnd(mark.anchor), geometry) - start;
        const width = Math.max(MIN_MARK_WIDTH_PX, span);
        return (
          <MarkButton
            key={mark.id}
            className={classNames(css.mark, {
              [css.markSelected]: mark.id === selectedCommentId,
            })}
            // Widened past its span, a mark is centred on that span rather than grown
            // rightwards; one wide enough to need no floor starts exactly where it starts.
            style={
              {
                left: start - (width - span) / 2,
                width,
                '--lane': lanes.get(mark.id) ?? 0,
              } as CSSProperties
            }
            label="Go to comment"
            onSelect={() => onSelect(mark.id)}
          />
        );
      })}
      {dotGroups.map((group) => {
        // Drawn at, and pointing to, its first comment in timeline order — the first of them in
        // the panel too.
        const [{ mark: lead, position }] = group;
        const count = group.length;
        return (
          <MarkButton
            key={lead.id}
            className={classNames(css.mark, css.markPoint, {
              [css.markCluster]: count > 1,
              [css.markSelected]: group.some(({ mark }) => mark.id === selectedCommentId),
            })}
            style={{ left: position }}
            label={count > 1 ? `${count} comments here` : 'Go to comment'}
            onSelect={() => onSelect(lead.id)}
          >
            {count > 1 && formatClusterCount(count)}
          </MarkButton>
        );
      })}
    </div>
  );
};

/** One pressable mark. Spans and dot groups differ in shape, not in how a press is handled. */
const MarkButton = ({
  className,
  style,
  label,
  onSelect,
  children,
}: {
  className: string;
  style: CSSProperties;
  label: string;
  onSelect: () => void;
  children?: ReactNode;
}) => (
  <button
    type="button"
    aria-label={label}
    className={className}
    style={style}
    // The strip under this reads a press as a scrub, which would drag the playhead to the mark's
    // own position before the click ever ran.
    onPointerDown={(event) => event.stopPropagation()}
    onClick={(event) => {
      event.stopPropagation();
      onSelect();
    }}
  >
    {children}
  </button>
);
